// The arithmetic behind the Expenses summary. Pure functions over the expense
// register (and the Annual Budget, for the watch-list), so the screen only lays
// the answers out. Budget-line spend reuses budgetLines, so the summary and the
// Budget vs Actual report can never disagree.

import { dateOf, isoOf, lastMonths, sumOf, totalBy, amountOf } from '../../utils/summaryStats';
import type { NamedTotal, MonthBucket } from '../../utils/summaryStats';
import type { BudgetDocument, Expense } from '../../types';
import { buildDocumentVariance, fyOfExpense, parseSplit, SPLIT } from './budgetLines';
import type { BudgetLineVariance } from './budgetLines';

const isVoid = (e: Expense) => e.status === 'VOID';
const blank = (v?: string | null) => !String(v ?? '').trim();

/** A line is "near" its budget from 85% used — the same band Budget vs Actual marks amber. */
export const NEAR_BUDGET_PCT = 85;

/**
 * Spend per payment mode. A split expense is shared out to the modes it was
 * paid from, so "Cash" is the cash that actually left the box; a split whose
 * parts are unreadable stays under "Split" rather than vanishing.
 */
export const spendByMode = (expenses: Expense[]): NamedTotal[] => {
  const map = new Map<string, NamedTotal>();
  const add = (mode: string, amount: number) => {
    const cur = map.get(mode) || { name: mode, count: 0, total: 0 };
    cur.count += 1;
    cur.total += amount;
    map.set(mode, cur);
  };
  expenses.forEach((e) => {
    const mode = String(e.paymentMode || '').trim() || 'Not set';
    const parts = mode === SPLIT ? parseSplit(e.splitPayments).filter((p) => p.mode && amountOf(p.amount) > 0) : [];
    if (parts.length) parts.forEach((p) => add(p.mode, amountOf(p.amount)));
    else add(mode, amountOf(e.amount));
  });
  return Array.from(map.values())
    .map((x) => ({ ...x, total: Math.round(x.total * 100) / 100 }))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
};

/** What paperwork an expense is missing, or [] when it is complete. */
export const missingProof = (e: Expense): string[] => {
  const out: string[] = [];
  if (blank(e.receiptNo)) out.push('No bill / receipt no.');
  const mode = String(e.paymentMode || 'Cash').trim();
  // Cash has no transaction reference, and a split keeps its detail in the parts.
  if (mode !== 'Cash' && mode !== SPLIT && blank(e.referenceNo)) out.push(`No ${mode} reference`);
  if (blank(e.approvedBy)) out.push('Not approved');
  return out;
};

export interface ExpenseGap { expense: Expense; missing: string[]; }

export interface ExpenseSummary {
  fy: string;
  thisMonth: number;
  lastMonth: number;
  fyTotal: number;
  fyCount: number;
  /** FY spend over the months of the year gone so far (the current one included). */
  monthlyAvg: number;
  allTime: number;
  liveCount: number;
  voided: number;
  months: MonthBucket[];
  byCategory: NamedTotal[];
  byMode: NamedTotal[];
  topPayees: NamedTotal[];
  /** Annual allowance across every line of this year's budget. */
  budgetTotal: number;
  /** Spend booked against those lines (excludes unbudgeted spend). */
  budgetSpent: number;
  /** Spend under a category or line the budget does not have. */
  unbudgeted: number;
  over: BudgetLineVariance[];
  near: BudgetLineVariance[];
  gaps: ExpenseGap[];
}

export const expenseSummary = (
  expenses: Expense[], budget: BudgetDocument | null | undefined, now: Date = new Date()
): ExpenseSummary => {
  const live = expenses.filter((e) => !isVoid(e));
  const today = isoOf(now);
  const fy = fyOfExpense(today);
  const fyRows = live.filter((e) => dateOf(e.date) && fyOfExpense(dateOf(e.date) as string) === fy);

  const thisYm = today.slice(0, 7);
  const lastYm = isoOf(new Date(now.getFullYear(), now.getMonth() - 1, 1)).slice(0, 7);
  const monthTotal = (ym: string) => sumOf(live.filter((e) => (dateOf(e.date) || '').startsWith(ym)), (e) => e.amount);

  const fyTotal = sumOf(fyRows, (e) => e.amount);
  // April is month 1 of the year, so September is month 6.
  const monthsGone = ((now.getMonth() + 9) % 12) + 1;

  const lines = buildDocumentVariance(budget, fy, live, [], now);
  const budgetLines = lines.filter((r) => r.kind === 'line');
  const usedPct = (r: BudgetLineVariance) => (r.budget > 0 ? (r.actual / r.budget) * 100 : 0);
  const byUse = (a: BudgetLineVariance, b: BudgetLineVariance) => usedPct(b) - usedPct(a);

  return {
    fy,
    thisMonth: monthTotal(thisYm),
    lastMonth: monthTotal(lastYm),
    fyTotal,
    fyCount: fyRows.length,
    monthlyAvg: Math.round(fyTotal / monthsGone),
    allTime: sumOf(live, (e) => e.amount),
    liveCount: live.length,
    voided: expenses.length - live.length,
    months: lastMonths(live, (e) => e.date, 12, now, (e) => e.amount),
    byCategory: totalBy(fyRows, (e) => e.category, (e) => e.amount),
    byMode: spendByMode(fyRows),
    topPayees: totalBy(fyRows.filter((e) => !blank(e.paidTo)), (e) => String(e.paidTo).trim(), (e) => e.amount).slice(0, 10),
    budgetTotal: Math.round(budgetLines.reduce((n, r) => n + r.budget, 0) * 100) / 100,
    budgetSpent: Math.round(budgetLines.reduce((n, r) => n + r.actual, 0) * 100) / 100,
    unbudgeted: Math.round(lines.filter((r) => r.kind === 'other' || r.kind === 'unbudgeted')
      .reduce((n, r) => n + r.actual, 0) * 100) / 100,
    over: budgetLines.filter((r) => r.budget > 0 && r.actual > r.budget).sort(byUse),
    near: budgetLines.filter((r) => r.budget > 0 && r.actual <= r.budget && usedPct(r) >= NEAR_BUDGET_PCT).sort(byUse),
    gaps: live
      .map((expense) => ({ expense, missing: missingProof(expense) }))
      .filter((g) => g.missing.length > 0)
      .sort((a, b) => String(dateOf(b.expense.date)).localeCompare(String(dateOf(a.expense.date))))
  };
};
